import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { accessTokenStore } from '../../auth/accessTokenStore';
import { useStore } from '../../../store';

/**
 * Wraps mobile routes that require an active session. If we have no in-memory
 * access token, we try a silent refresh once (the refresh-token cookie may
 * still be valid after a page reload). Only if that fails do we redirect to
 * the setup page where the user must scan a fresh QR code.
 */
export default function MobileGuard({ children }: { children: ReactNode }) {
  const [state, setState] = useState<'checking' | 'ok' | 'expired'>(() =>
    accessTokenStore.get() ? 'ok' : 'checking'
  );

  useEffect(() => {
    if (state !== 'checking') {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        await useStore.getState().refreshAccessToken();
        if (!cancelled) {
          setState('ok');
        }
      } catch {
        if (!cancelled) {
          setState('expired');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [state]);

  if (state === 'checking') {
    return (
      <div className="flex items-center justify-center min-h-dvh">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (state === 'expired') {
    return <Navigate to="/m/setup" replace state={{ reason: 'expired' }} />;
  }

  return <>{children}</>;
}
